import { useEffect, useState } from "react";
import { Share2 } from "lucide-react";
import { formatDuration } from "@/lib/format";

export default function Header({ competition, onRun, onReset, onShare, running, spectator }) {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const secondsRemaining = (() => {
    if (!competition?.end_time) return 0;
    const end = new Date(competition.end_time).getTime();
    return Math.max(0, Math.floor((end - now) / 1000));
  })();

  const dayLabel = competition
    ? `DAY ${competition.day_number} / ${competition.total_days}`
    : "INITIALIZING…";
  const status = competition?.status || "loading";

  return (
    <header
      data-testid="arena-header"
      className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-arena-line"
    >
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center gap-6">
        <a href="/" className="flex items-center gap-3 no-underline text-arena-ink">
          <div className="w-8 h-8 bg-arena-ink flex items-center justify-center">
            <span className="font-display font-black text-white text-sm tracking-tighter">A</span>
          </div>
          <div>
            <div className="font-display font-black text-xl tracking-tighter leading-none">
              AI TRADING ARENA
            </div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft mt-1">
              {spectator
                ? "Spectator view · live read-only feed"
                : "Three minds. Ten thousand each. Seven days."}
            </div>
          </div>
        </a>

        <div className="hidden md:flex items-center gap-2 ml-2">
          <span className={`w-2 h-2 ${status === "running" ? "bg-profit live-dot" : "bg-arena-mute"}`} />
          <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-ink">
            {status === "running" ? "LIVE" : status === "ended" ? "ENDED" : "—"}
          </span>
          {spectator && (
            <span className="ml-2 px-2 py-[3px] text-[10px] font-mono font-bold tracking-[0.2em] bg-arena-ink text-white uppercase">
              Spectator
            </span>
          )}
        </div>

        <div className="ml-auto flex items-center gap-6 flex-wrap">
          <div className="text-right" data-testid="day-indicator">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
              Competition
            </div>
            <div className="font-display font-black text-base tracking-tight">{dayLabel}</div>
          </div>
          <div className="text-right" data-testid="countdown">
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-arena-soft">
              Time remaining
            </div>
            <div className="font-mono font-bold text-base tabular-nums">
              {formatDuration(secondsRemaining)}
            </div>
          </div>

          {!spectator && (
            <>
              <button
                data-testid="run-round-btn"
                onClick={onRun}
                disabled={running}
                className="rounded-none bg-arena-ink text-white hover:bg-black/80 disabled:opacity-50 font-bold uppercase tracking-[0.2em] text-[11px] py-3 px-5 transition-colors"
              >
                {running ? "Running…" : "Run Round"}
              </button>
              <button
                data-testid="reset-btn"
                onClick={onReset}
                className="rounded-none border border-arena-line bg-white text-arena-ink hover:bg-arena-alt font-bold uppercase tracking-[0.2em] text-[11px] py-3 px-5 transition-colors"
              >
                Reset
              </button>
            </>
          )}

          <button
            data-testid="share-btn"
            onClick={onShare}
            className="rounded-none border border-arena-ink bg-arena-ink text-white hover:bg-black/80 font-bold uppercase tracking-[0.2em] text-[11px] py-3 px-5 transition-colors flex items-center gap-2"
            title="Share spectator URL"
          >
            <Share2 size={14} />
            Share
          </button>
        </div>
      </div>
    </header>
  );
}
